import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { amiGet, amiPost, Card, Loading, NotYet, isoFmt, errMsg } from "@/lib/ami";

const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINTS = ["status", "market?inst=BTC-USDT-SWAP", "account", "history/coverage", "predictions", "risk-events", "logs"];

function NewToken({ onCreated }) {
  const [name, setName] = useState("");
  const [ttl, setTtl] = useState(24);
  const m = useMutation({ mutationFn: () => amiPost("/monitoring/tokens", { name, ttl_hours: Number(ttl) }), onSuccess: onCreated, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block"><span className="text-xs text-muted-foreground">Name</span><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. ChatGPT" className="mt-1 w-52" data-testid="ami-token-name" /></label>
      <label className="block"><span className="text-xs text-muted-foreground">Gültig (Stunden, max. 720)</span><Input type="number" min={1} max={720} value={ttl} onChange={(e) => setTtl(e.target.value)} className="mt-1 w-32" data-testid="ami-token-ttl" /></label>
      <Button onClick={() => m.mutate()} disabled={!name || m.isPending} data-testid="ami-token-create">Zugang erstellen</Button>
    </div>
  );
}

function AuditLog() {
  const q = useQuery({ queryKey: ["ami-audit"], queryFn: () => amiGet("/audit", { limit: 60 }), refetchInterval: 15000 });
  if (q.isLoading) return <Loading />;
  return (
    <div className="max-h-96 overflow-y-auto text-xs font-mono space-y-1" data-testid="ami-audit-log">
      {q.data.map((a) => <div key={a.id} className="text-muted-foreground"><span className="text-foreground/80">{isoFmt(a.at)}</span> · {a.actor} · {a.action}{a.env ? ` [${a.env}]` : ""} · {JSON.stringify(a.details)}</div>)}
    </div>
  );
}

export default function AmiMonitoring() {
  const qc = useQueryClient();
  const [fresh, setFresh] = useState(null);
  const q = useQuery({ queryKey: ["ami-tokens"], queryFn: () => amiGet("/monitoring/tokens") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["ami-tokens"] });
  const revoke = (id) => amiPost(`/monitoring/tokens/${id}/revoke`).then(refresh).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-6" data-testid="ami-monitoring">
      <Card title="Read-only Monitoring API" testid="ami-monitoring-api">
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">Für eine spätere ChatGPT- oder MCP-Anbindung. Strikt lesend, kurzlebige widerrufbare Tokens, keine Secrets, keine Handelsrechte. Jeder Zugriff wird protokolliert.</p>
        <div className="mt-4"><NewToken onCreated={(t) => { setFresh(t); refresh(); }} /></div>
        {fresh && (
          <div className="mt-4 rounded-lg border border-amber-400/40 bg-amber-400/5 p-4" data-testid="ami-token-fresh">
            <div className="text-xs text-amber-300 mb-2">Nur jetzt sichtbar – sicher speichern:</div>
            <div className="flex items-center gap-2"><code className="font-mono text-xs break-all" data-testid="ami-token-value">{fresh.token}</code>
              <Button size="icon" variant="ghost" onClick={() => navigator.clipboard.writeText(fresh.token).then(() => toast.success("Kopiert"))} data-testid="ami-token-copy"><Copy className="h-4 w-4" /></Button></div>
          </div>
        )}
        <div className="mt-6 space-y-2">
          {q.isLoading ? <Loading /> : q.data.length === 0 ? <NotYet text="Noch kein Monitoring-Zugang." /> : q.data.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-border/60 pb-2" data-testid={`ami-token-${t.id}`}>
              <span>{t.name} <span className="font-mono text-xs text-muted-foreground">…{t.hint}</span></span>
              <span className="text-xs text-muted-foreground">{t.revoked ? "widerrufen" : new Date(t.expires_at) < new Date() ? "abgelaufen" : `gültig bis ${isoFmt(t.expires_at)}`} · {t.uses} Abrufe</span>
              {!t.revoked && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => revoke(t.id)} data-testid={`ami-token-revoke-${t.id}`}>Widerrufen</Button>}
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-muted-foreground font-mono space-y-1">
          <div className="font-sans">Endpunkte (Header: Authorization: Bearer &lt;Token&gt;):</div>
          {ENDPOINTS.map((e) => <div key={e}>GET {API}/api/ami/monitor/{e}</div>)}
        </div>
      </Card>
      <Card title="Audit-Log" testid="ami-audit-card"><AuditLog /></Card>
    </div>
  );
}
